'use client'
import 'react-phone-number-input/style.css'
import { useSignUp } from '@clerk/nextjs'
import { useEffect, useState } from 'react'
import Link from 'next/link'
import Image from "next/image"
import * as Clerk from '@clerk/elements/common'
import * as SignUp from '@clerk/elements/sign-up'
import { AnimatePresence, motion } from 'framer-motion';
import { KeenIcon } from '../common/keenicons'
import clsx from 'clsx'
import { useIntl, FormattedMessage } from 'react-intl';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import { ScreenLoader } from '../common'



const SignUpForm: React.FC = () => {
    const { formatMessage } = useIntl();
    const intl = useIntl();
    const { isLoaded } = useSignUp()
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)

    const [firstName, setFirstName] = useState('');
    const [firstNameError, setFirstNameError] = useState('');

    const [lastName, setLastName] = useState('');
    const [lastNameError, setLastNameError] = useState('');

    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');

    const [phone, setPhone] = useState<string | undefined>('');
    const [phoneError, setPhoneError] = useState('');

    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');

    const [confirmPassword, setConfirmPassword] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');

    const [formValid, setFormValid] = useState(false);
    function validateEmail(email: string): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    const termsURL = process.env.NEXT_PUBLIC_STORE_URL;


    const validatePhone = (value: string | undefined) => {
        if (!value) {
            setPhoneError(intl.formatMessage({ id: 'VALIDATION.PHONE_REQUIRED' }));
        } else if (!isValidPhoneNumber(value)) {
            setPhoneError(intl.formatMessage({ id: 'VALIDATION.PHONE_INVALID_NUMBER' }));
        } else {
            setPhoneError('');
        }
    };

    useEffect(() => {
        const noErrors =
            !firstNameError &&
            !lastNameError &&
            !emailError &&
            !phoneError &&
            !passwordError &&
            !confirmPasswordError;

        const allFilled =
            firstName &&
            lastName &&
            email &&
            phone &&
            password &&
            confirmPassword;

        setFormValid(Boolean(noErrors && allFilled));
    }, [
        firstNameError,
        lastNameError,
        emailError,
        phoneError,
        passwordError,
        confirmPasswordError,
        firstName,
        lastName,
        email,
        phone,
        password,
        confirmPassword
    ]);

    if (!isLoaded) return <ScreenLoader />;

    return (
        <SignUp.Root routing="virtual">
            <Clerk.Loading>
                {(isGlobalLoading) => (
                    <>
                        <SignUp.Step
                            name="start"
                            className="card max-w-[504px] w-full"
                        >

                            <div className="card-body flex flex-col gap-5 p-10">
                                <div className="text-center mb-2.5">
                                    <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5"><FormattedMessage id="SIGN_IN.SIGN_UP_TEXT" /></h3>
                                    <div className="flex items-center justify-center font-medium">
                                        <span className="text-2sm text-gray-600 me-1.5"><FormattedMessage id="SIGNUP_ALREADY_ACCOUNT" /> </span>
                                        <Link href="/sign-in" className="text-2sm link"><FormattedMessage id="SIGN_IN.SIGN_IN_TEXT" /></Link>
                                    </div>
                                </div>
                                <Clerk.GlobalError
                                    role="alert"
                                    className="flex items-center gap-2.5 border rounded-md p-3 border-danger-clarity bg-danger-light text-danger"
                                >
                                    {(error) => (
                                        <div className="flex items-center gap-2.5">
                                            <KeenIcon icon="information-2" className="text-lg leading-none text-danger" />
                                            <div className="text-gray-700 text-sm">{error.message}</div>
                                        </div>
                                    )}
                                </Clerk.GlobalError>
                                {/* Social Sign Up */}
                                <div className="grid grid-cols-2 gap-2.5">
                                    <Clerk.Connection
                                        name="google"
                                        className="btn btn-light btn-sm justify-center"
                                    >
                                        <Image
                                            src="/assets/media/brand-logos/google.svg"
                                            alt="Google"
                                            width={16}
                                            height={16}
                                            className="size-3.5 shrink-0"
                                        />
                                        <FormattedMessage id="USE_GOOGLE" />
                                    </Clerk.Connection>
                                    <Clerk.Connection
                                        name="apple"
                                        className="btn btn-light btn-sm justify-center"
                                    >
                                        <Image src="/assets/media/brand-logos/apple-black.svg" className="size-3.5 shrink-0 dark:hidden" alt="Apple" width={16} height={16} />
                                        <Image src="/assets/media/brand-logos/apple-white.svg" className="size-3.5 shrink-0 light:hidden" alt="Apple" width={16} height={16} />
                                        <FormattedMessage id="USE_APPLE" />
                                    </Clerk.Connection>
                                </div>

                                <div className="flex items-center gap-2">
                                    <span className="border-t border-gray-200 w-full"></span>
                                    <span className="text-2xs text-gray-500 font-medium uppercase"><FormattedMessage id="OR" /></span>
                                    <span className="border-t border-gray-200 w-full"></span>
                                </div>

                                <div className="flex flex-col gap-6 w-full">

                                    {/* Row 1: First Name / Last Name */}
                                    <div className="flex gap-6 sm:flex-row flex-col w-full">
                                        {/* First Name */}
                                        <Clerk.Field name="firstName" className="flex flex-col gap-1">
                                            <Clerk.Label className="form-label text-gray-900"><FormattedMessage id="FIRST_NAME" /></Clerk.Label>
                                            <label htmlFor="" className="input">
                                                <Clerk.Input
                                                    type="text"
                                                    className="form-control bg-transparent"
                                                    placeholder={formatMessage({ id: 'FIRST_NAME_PLACEHOLDER' })}
                                                    value={firstName}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setFirstName(value);
                                                        if (value) setFirstNameError('');
                                                    }}
                                                    onBlur={(e) => {
                                                        const value = e.target.value;
                                                        setFirstNameError(value ? '' : formatMessage({ id: 'VALIDATION.FIRST_NAME_REQUIRED' }));
                                                    }}
                                                />
                                            </label>
                                            {firstNameError && <span className="text-danger text-xs mt-1">{firstNameError}</span>}
                                            <Clerk.FieldError className="text-danger text-xs mt-1" />
                                        </Clerk.Field>

                                        {/* Last Name */}
                                        <Clerk.Field name="lastName" className="flex flex-col gap-1">
                                            <Clerk.Label className="form-label text-gray-900"><FormattedMessage id="LAST_NAME" /></Clerk.Label>
                                            <label htmlFor="" className="input">
                                                <Clerk.Input
                                                    type="text"
                                                    className="form-control bg-transparent"
                                                    placeholder={formatMessage({ id: 'LAST_NAME_PLACEHOLDER' })}
                                                    value={lastName}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setLastName(value);
                                                        if (value) setLastNameError('');
                                                    }}
                                                    onBlur={(e) => {
                                                        const value = e.target.value;
                                                        setLastNameError(value ? '' : formatMessage({ id: 'VALIDATION.LAST_NAME_REQUIRED' }));
                                                    }}
                                                /></label>
                                            {lastNameError && <span className="text-danger text-xs mt-1">{lastNameError}</span>}
                                            <Clerk.FieldError className="text-danger text-xs mt-1" />
                                        </Clerk.Field>
                                    </div>

                                    {/* Row 2: Email / Phone Number */}
                                    <div className="flex gap-6 sm:flex-row flex-col w-full">
                                        {/* Email */}
                                        <Clerk.Field name="emailAddress" className="flex flex-col gap-1">
                                            <Clerk.Label className="form-label text-gray-900"><FormattedMessage id="EMAIL" /></Clerk.Label>
                                            <label htmlFor="" className="input">
                                                <Clerk.Input
                                                    type="email"
                                                    className="form-control bg-transparent"
                                                    placeholder={formatMessage({ id: 'EMAIL_PLACEHOLDER' })}
                                                    value={email}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setEmail(value);
                                                        if (value && validateEmail(value)) {
                                                            setEmailError('');
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        const value = e.target.value;
                                                        if (!value) {
                                                            setEmailError(formatMessage({ id: 'VALIDATION.EMAIL_REQUIRED' }));
                                                        } else if (!validateEmail(value)) {
                                                            setEmailError(formatMessage({ id: 'VALIDATION.EMAIL_INVALID' }));
                                                        } else {
                                                            setEmailError('');
                                                        }
                                                    }}
                                                /></label>
                                            {emailError && <span className="text-danger text-xs mt-1">{emailError}</span>}
                                            <Clerk.FieldError className="text-danger text-xs mt-1" />
                                        </Clerk.Field>

                                        {/* Phone Number (optional) */}
                                        <Clerk.Field name="phoneNumber" className="flex flex-col gap-1">
                                            <Clerk.Label className="form-label text-gray-900"> <FormattedMessage id="PHONE_NUMBER" /></Clerk.Label>
                                            <label htmlFor="" className="input">
                                                <div className='form-control bg-transparent max-w-[174px]'>
                                                    <PhoneInput
                                                        international
                                                        value={phone}
                                                        onChange={(val) => setPhone(val)}
                                                        onBlur={() => validatePhone(phone)}
                                                        placeholder={formatMessage({ id: 'PHONE_NUMBER_PLACEHOLDER' })}
                                                    />
                                                </div>
                                            </label>
                                            <Clerk.Input
                                                type="hidden"
                                                name="phoneNumber"
                                                value={phone}
                                            />
                                            {phoneError && <span className="text-danger text-xs mt-1">{phoneError}</span>}
                                            <Clerk.FieldError className="text-danger text-xs mt-1" />
                                        </Clerk.Field>
                                    </div>


                                    {/* Row 3: Password / Confirm Password */}
                                    <div className="flex gap-6 sm:flex-row flex-col w-full">
                                        {/* Password */}
                                        <Clerk.Field name="password" className="flex flex-col gap-1">
                                            <Clerk.Label className="form-label text-gray-900"><FormattedMessage id="PASSWORD" /></Clerk.Label>
                                            <label htmlFor="" className="input">
                                                <Clerk.Input
                                                    type={showPassword ? 'text' : 'password'}
                                                    className="form-control bg-transparent w-full"
                                                    placeholder={formatMessage({ id: 'PASSWORD_PLACEHOLDER' })}
                                                    value={password}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setPassword(value);
                                                        if (value) setPasswordError('');
                                                    }}
                                                    onBlur={(e) => {
                                                        const value = e.target.value;
                                                        setPasswordError(value ? '' : formatMessage({ id: 'VALIDATION.PASSWORD_REQUIRED' }));
                                                    }}
                                                />
                                                <button type="button" onClick={() => setShowPassword(!showPassword)} className="btn btn-icon" >
                                                    <KeenIcon icon="eye" className={clsx('text-gray-500', { hidden: showPassword })} />
                                                    <KeenIcon
                                                        icon="eye-slash"
                                                        className={clsx('text-gray-500', { hidden: !showPassword })}
                                                    />
                                                </button></label>
                                            {passwordError && <span className="text-danger text-xs mt-1">{passwordError}</span>}
                                            <Clerk.FieldError className="text-danger text-xs mt-1" />
                                        </Clerk.Field>

                                        {/* Confirm Password */}
                                        <Clerk.Field name="confirmPassword" className="flex flex-col gap-1">
                                            <Clerk.Label className="form-label text-gray-900"><FormattedMessage id="CONFIRM_PASSWORD" /></Clerk.Label>
                                            <label htmlFor="" className="input">
                                                <Clerk.Input
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    className="form-control bg-transparent w-full"
                                                    placeholder={formatMessage({ id: 'CONFIRM_PASSWORD_PLACEHOLDER' })}
                                                    value={confirmPassword}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setConfirmPassword(value);
                                                        if (value && value === password) {
                                                            setConfirmPasswordError('');
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        const value = e.target.value;
                                                        if (!value) {
                                                            setConfirmPasswordError(formatMessage({ id: 'VALIDATION.CONFIRM_PASSWORD_REQUIRED' }));
                                                        } else if (value !== password) {
                                                            setConfirmPasswordError(formatMessage({ id: 'PASSWORD_NOT_MATCH' }));
                                                        } else {
                                                            setConfirmPasswordError('');
                                                        }
                                                    }}
                                                />
                                                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="btn btn-icon">
                                                    <KeenIcon icon="eye" className={clsx('text-gray-500', { hidden: showConfirmPassword })} />
                                                    <KeenIcon
                                                        icon="eye-slash"
                                                        className={clsx('text-gray-500', { hidden: !showConfirmPassword })}
                                                    />
                                                </button></label>
                                            {confirmPasswordError && <span className="text-danger text-xs mt-1">{confirmPasswordError}</span>}
                                            <Clerk.FieldError className="text-danger text-xs mt-1" />
                                        </Clerk.Field>
                                    </div>

                                </div>
                                {/* Terms and Conditions */}
                                <label className="checkbox-group">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-sm"
                                    />
                                    <span className="checkbox-label">
                                        <FormattedMessage id="SIGN_UP.I_ACCEPT" />
                                        <Link href={`${termsURL || '#'}/terms`} className="text-2sm link">
                                            <FormattedMessage id="SIGN_UP.TERM_AND_CONDITION" /></Link>
                                    </span>
                                </label>

                                {/* Submit */}
                                <SignUp.Action submit asChild>
                                    <button disabled={!formValid || isGlobalLoading} className="btn btn-primary flex justify-center grow">
                                        <Clerk.Loading>
                                            {(isLoading) => {
                                                return isLoading ? (
                                                    <FormattedMessage id="LOADING" />
                                                ) : (
                                                    <FormattedMessage id="EMAIL_CODE.CONTINUE" />
                                                )
                                            }}
                                        </Clerk.Loading>
                                    </button>
                                </SignUp.Action>

                                {/* Error Message */}

                                <SignUp.Captcha className="empty:hidden" />
                            </div>

                        </SignUp.Step>


                        <SignUp.Step name="continue">
                            <h2 className="text-xl font-bold mb-4"><FormattedMessage id="SIGN_UP.COMPLETE_YOUR_PROFILE" /></h2>
                            <Clerk.Field name="phoneNumber">
                                <Clerk.Label><FormattedMessage id="SIGN_UP.PHONE_NUMBER" /></Clerk.Label>
                                <Clerk.Input />
                                <Clerk.FieldError />
                            </Clerk.Field>
                            <SignUp.Action submit><FormattedMessage id="SIGN_UP.CONTINUE" /></SignUp.Action>
                        </SignUp.Step>



                        <SignUp.Step
                            name="verifications" className="card max-w-[440px] w-full">
                            <Clerk.GlobalError
                                role="alert"
                                className="flex items-center gap-2.5 border rounded-md p-3 border-danger-clarity bg-danger-light text-danger"
                            >
                                {(error) => (
                                    <div className="flex items-center gap-2.5">
                                        <KeenIcon icon="information-2" className="text-lg leading-none text-danger" />
                                        <div className="text-gray-700 text-sm">{error.message}</div>
                                    </div>
                                )}
                            </Clerk.GlobalError>

                            <div className="card-body p-10">
                                <div className="flex justify-center py-10">
                                    <Image
                                        width={200}
                                        height={200}
                                        alt="image"
                                        src="/assets/media/illustrations/30.svg"
                                        className="dark:hidden max-h-[130px]"
                                    />
                                    <Image src="/assets/media/illustrations/30-dark.svg" className="light:hidden max-h-[130px]" alt="" width={200}
                                        height={200} />
                                </div>

                                <SignUp.Strategy name="email_code" >
                                    <h3 className="text-lg font-medium text-gray-900 text-center mb-3 "><FormattedMessage id="VERIFY_EMAIL.CHECK_YOUR_EMAIL" /></h3>
                                    <div className='mx-14'>
                                        <div className="text-2sm text-center text-gray-700 mb-7.5"> <FormattedMessage id="VERIFY_EMAIL.MESSAGE1" /> <br /><FormattedMessage id="VERIFY_EMAIL.MESSAGE2" /></div>
                                    </div>

                                    <Clerk.Field name="code">

                                        <Clerk.Input
                                            type="otp"
                                            required
                                            className="flex justify-center gap-1 my-3"
                                            render={({ value, status }) => (
                                                <div
                                                    data-status={status}
                                                    className="relative h-9 w-8 rounded-md bg-white ring-1 ring-inset ring-zinc-300 data-[status=selected]:bg-sky-400/10 data-[status=selected]:shadow-[0_0_8px_2px_theme(colors.sky.400/30%)] data-[status=selected]:ring-sky-400 
                                                    dark:bg-[--tw-page-bg-dark]"
                                                >
                                                    <AnimatePresence>
                                                        {value && (
                                                            <motion.span
                                                                initial={{ opacity: 0, scale: 0.75 }}
                                                                animate={{ opacity: 1, scale: 1 }}
                                                                exit={{ opacity: 0, scale: 0.75 }}
                                                                className="absolute inset-0 flex items-center justify-center text-zinc-950 dark:text-[--tw-page-text-dark]"
                                                            >
                                                                {value}
                                                            </motion.span>
                                                        )}
                                                        {value}
                                                    </AnimatePresence>
                                                    {status === 'cursor' && (
                                                        <motion.div
                                                            layoutId="otp-input-focus"
                                                            transition={{ ease: [0.2, 0.4, 0, 1], duration: 0.2 }}
                                                            className="absolute inset-0 z-10 rounded-[inherit] border border-sky-400 bg-sky-400/10 shadow-[0_0_8px_2px_theme(colors.sky.400/30%)]"
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        />
                                        <Clerk.FieldError className="text-danger text-xs mt-1" />
                                    </Clerk.Field>
                                    <div className='flex justify-center mb-5'>
                                        <SignUp.Action
                                            submit
                                            className='btn btn-primary flex justify-center'
                                        >
                                            <FormattedMessage id="VERIFY_AND_CONTINUE" />
                                        </SignUp.Action></div>
                                </SignUp.Strategy>
                                <div className="flex items-center justify-center gap-1">
                                    <span className="text-xs text-gray-700">
                                        <FormattedMessage id="DIDNT_GET_CODE" /> </span>
                                    <SignUp.Action navigate="start" className="text-xs font-medium link"><FormattedMessage id="SIGN_IN.SIGN_UP_TEXT" /></SignUp.Action>
                                </div>
                            </div>
                        </SignUp.Step>
                    </>
                )}
            </Clerk.Loading>

        </SignUp.Root>
    )
}

export default SignUpForm

